import { resolveClientId, type ClientBook } from "@/features/intelligence/lib/client-id-resolve";
import type { HandlerOutcome } from "@/features/intelligence/lib/frontend-tool-run";
import type { RecordSubjectRef } from "@/features/forms/shared/record/record-route";

type StartAdvisorySubject = RecordSubjectRef & { clientLabel: string | null };

export type StartAdvisoryPlan =
  | { press: true; subject: StartAdvisorySubject | null }
  | { press: false; message: string };

export async function planStartAdvisory(
  clientId: string | null,
  book: ClientBook,
): Promise<StartAdvisoryPlan> {
  if (clientId === null) {
    return { press: true, subject: null };
  }

  const resolved = await resolveClientId(clientId, book);
  if (!resolved.ok) {
    return { press: false, message: resolved.message };
  }

  const { row } = resolved;
  return {
    press: true,
    subject: { kind: row.kind, subjectId: row.partyId, clientLabel: row.name },
  };
}

export function reportStartAdvisory(
  plan: Extract<StartAdvisoryPlan, { press: true }>,
): HandlerOutcome {
  const asked = plan.subject;
  if (asked === null) {
    return {
      ok: true,
      message:
        "The advisory picker is now open, with no client preselected — the " +
        "advisor chooses the client there. This changed the view only: no " +
        "advisory, portfolio or optimisation has been created, and none will be " +
        "until the advisor works further through the picker themselves. Do not " +
        "tell the advisor that an advisory exists.",
    };
  }

  const who = asked.clientLabel ?? "the client that was asked for";
  return {
    ok: true,
    message:
      `The advisory picker is now open with ${who} preselected as the client. ` +
      "This changed the view only: no advisory, portfolio or optimisation has " +
      "been created, and none will be until the advisor works further through " +
      "the picker themselves. Do not tell the advisor that an advisory exists.",
  };
}
